import { Bath, Bed, SaveHeart, Share } from "@/components/bnp-icons";
import { getPropertyType } from "@/components/pages/user/dashboard/properties/CreatePropertyForm";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter";
import {
  LandTitleDocument,
  LandTitleDocumentLabels,
} from "@/types/property-config";
import { Property } from "@/types/property";
import { Fragment } from "react";

interface PropDetailsSemanticsProps {
  property: Property;
  isSaved: boolean;
  onShare: () => void;
  onSave: () => void;
}

const PropDetailsSemantics = ({
  property,
  isSaved,
  onShare,
  onSave,
}: PropDetailsSemanticsProps) => {
  const formatListingType = (listingType?: string) => {
    if (!listingType) return "";
    if (listingType === "sale") return "For Sale";
    if (listingType === "rent") return "For Rent";
    if (listingType === "shortlet") return "Shortlet";
    if (listingType === "lease") return "For Lease";
    if (listingType === "land") return "Land";
    return capitalizeFirstLetter(listingType.replace(/-/g, " "));
  };

  const formatTitleDocument = (value: string) => {
    const landLabel = LandTitleDocumentLabels[value as LandTitleDocument];
    if (landLabel) return landLabel;
    return value
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const isSale = property?.listing_type === "sale";
  const titleDocument =
    isSale &&
    ((property as any)?.sale_document_details || property?.land_title_document)
      ? (((property as any)?.sale_document_details ||
          property?.land_title_document) as string)
      : undefined;

      console.log("titleDocument", titleDocument)

  // --- Category-gated semantics -------------------------------------------
  // All categories share one table, so stray values (e.g. bedroom_count on a
  // land record from weak sanitization) must be hidden unless the field is
  // relevant to the property's category. Source of truth: AddPropertyPayload
  // comments in types/property.ts + CreateNewListingV2 inputs.
  //   bedroom/bathroom/toilet -> RESIDENTIAL only
  //   furnished/serviced/shared -> RESIDENTIAL or COMMERCIAL only
  //   number_of_plots -> LAND only
  const rawCategory = (property?.property_category ?? "")
    .toString()
    .trim()
    .toUpperCase();
  // Legacy fallback: old records may carry listing_type "land" with no category.
  const category =
    rawCategory ||
    (property?.listing_type?.toLowerCase() === "land" ? "LAND" : "");
  const isLand = category === "LAND";
  const isResidential = category === "RESIDENTIAL";
  const isCommercial = category === "COMMERCIAL";

  // Unknown/missing category: keep legacy (ungated) rendering so older
  // records without property_category don't go blank.
  const hasKnownCategory = category !== "";
  const canShowRoomCounts = isResidential || !hasKnownCategory;
  const canShowFurnishedServicedShared =
    isResidential || isCommercial || !hasKnownCategory;

  const hasCount = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value) && value > 0;

  const semantics: React.ReactNode[] = [];

  if (canShowRoomCounts && hasCount(property?.bedroom_count)) {
    semantics.push(
      <div className="flex items-center gap-1 sm:gap-2">
        <Bed />
        <span className="whitespace-nowrap">
          {property.bedroom_count}{" "}
          {property.bedroom_count === 1 ? "Bed" : "Beds"}
        </span>
      </div>,
    );
  }
  if (canShowRoomCounts && hasCount(property?.bathroom_count)) {
    semantics.push(
      <div className="flex items-center gap-1 sm:gap-2">
        <Bath />
        <span className="whitespace-nowrap">
          {property.bathroom_count}{" "}
          {property.bathroom_count === 1 ? "Bath" : "Baths"}
        </span>
      </div>,
    );
  }
  if (canShowRoomCounts && hasCount(property?.toilet)) {
    semantics.push(
      <div className="flex items-center gap-1 sm:gap-2">
        <Bath />
        <span className="whitespace-nowrap">
          {property.toilet} {property.toilet === 1 ? "Toilet" : "Toilets"}
        </span>
      </div>,
    );
  }
  if (isLand && hasCount(property?.number_of_plots)) {
    semantics.push(
      <span className="whitespace-nowrap">
        {property.number_of_plots}{" "}
        {property.number_of_plots === 1 ? "SQM" : "SQMs"}
      </span>,
    );
  }
  if (canShowFurnishedServicedShared && property?.serviced) {
    semantics.push(<span className="whitespace-nowrap">Serviced</span>);
  }
  if (canShowFurnishedServicedShared && property?.shared) {
    semantics.push(<span className="whitespace-nowrap">Shared</span>);
  }
  if (canShowFurnishedServicedShared && property?.furnished) {
    semantics.push(<span className="whitespace-nowrap">Furnished</span>);
  }

  return (
    <>
      {/* LISTING TYPE & PROPERTY TYPE BADGES */}
      <div className="flex flex-wrap items-center gap-2 mt-6">
        {property?.listing_type && (
          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-bnp-cyan text-white capitalize">
            {formatListingType(property.listing_type)}
          </span>
        )}
        {property?.property_type && (
          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-weak-100 text-main-900 border border-soft-200">
            {getPropertyType(property.property_type) ||
              capitalizeFirstLetter(
                property.property_type?.replace(/-/g, " "),
                2,
              )}
          </span>
        )}
        {property?.property_stage && (
          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-weak-100 text-grey-7 border border-soft-200 capitalize">
            {property.property_stage}
          </span>
        )}
        {titleDocument && (
          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-weak-100 text-main-900 border border-soft-200">
            Title Document: {formatTitleDocument(titleDocument)}
          </span>
        )}
      </div>

      {/* BEDROOMS / BATHROOMS / TAGS + SHARE & SAVE */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm font-bold text-soft-400 mt-4 border-b border-soft-200 pb-4">
        {semantics.map((item, index) => (
          <Fragment key={index}>
            {index > 0 && <div className="border-r border-grey-5 h-4" />}
            {item}
          </Fragment>
        ))}

        {/* SHARE & SAVE */}
        <div className="flex items-center gap-2 sm:gap-4 sm:ml-auto shrink-0">
          <button
            onClick={onShare}
            className="flex items-center gap-1 text-sm text-grey-7 hover:text-main-900 transition-colors"
            aria-label="Share property"
          >
            <span>Share</span>
            <Share size={18} />
          </button>
          <button
            onClick={onSave}
            className="flex items-center gap-1 text-sm text-grey-7 hover:text-main-900 transition-colors"
            aria-label="Save property"
          >
            <span>Save</span>
            <SaveHeart saved={isSaved} size={18} />
          </button>
        </div>
      </div>
    </>
  );
};

export default PropDetailsSemantics;
